import React from 'react';
import { Bell, CheckCircle, RefreshCw, Trash2, BookOpen, Briefcase, Clock, Mail } from 'lucide-react';
import { toast } from 'sonner';
import apiClient from '../../../services/apiClient';
import { Button } from '../../../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../../components/ui/card';
import { Badge } from '../../../components/ui/badge';
import { TabsContent } from '../../../components/ui/tabs';

const NotificationsTab = ({ notifications, unreadNotifications, fetchNotifications }) => (
    <TabsContent value="notifications">
        <Card>
            <CardHeader>
                <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                    <div>
                        <CardTitle className="flex items-center">
                            <Bell className="h-5 w-5 mr-2 text-blue-600" />
                            Fulfilled Notifications
                        </CardTitle>
                        <CardDescription>
                            Track when users mark their posts or applications as fulfilled
                        </CardDescription>
                    </div>
                    <div className="flex items-center space-x-3">
                        <Button
                            variant="outline"
                            size="sm"
                            onClick={fetchNotifications}
                        >
                            <RefreshCw className="h-4 w-4 mr-2" />
                            Refresh
                        </Button>
                        {unreadNotifications > 0 && (
                            <Button
                                size="sm"
                                onClick={async () => {
                                    try {
                                        await apiClient.post('/admin/notifications/mark-all-read');
                                        fetchNotifications();
                                        toast.success('All notifications marked as read');
                                    } catch (error) {
                                        toast.error('Failed to mark notifications as read');
                                    }
                                }}
                            >
                                <CheckCircle className="h-4 w-4 mr-2" />
                                Mark All Read
                            </Button>
                        )}
                    </div>
                </div>
            </CardHeader>
            <CardContent>
                {notifications.length === 0 ? (
                    <div className="text-center py-12">
                        <Bell className="h-12 w-12 mx-auto text-gray-300 mb-4" />
                        <p className="text-gray-500">No notifications yet</p>
                        <p className="text-sm text-gray-400">You'll see notifications here when users mark posts as fulfilled</p>
                    </div>
                ) : (
                    <div className="space-y-4">
                        {notifications.map((notif) => (
                            <div
                                key={notif.id}
                                className={`p-4 rounded-lg border ${notif.is_read ? 'bg-gray-50 border-gray-200' : 'bg-blue-50 border-blue-200'}`}
                            >
                                <div className="flex items-start justify-between">
                                    <div className="flex items-start space-x-3">
                                        <div className={`w-10 h-10 rounded-full flex items-center justify-center ${notif.type === 'tuition_fulfilled' ? 'bg-green-100' :
                                            notif.type === 'job_fulfilled' ? 'bg-blue-100' : 'bg-purple-100'
                                            }`}>
                                            {notif.type === 'tuition_fulfilled' ? (
                                                <BookOpen className="h-5 w-5 text-green-600" />
                                            ) : notif.type === 'job_fulfilled' ? (
                                                <Briefcase className="h-5 w-5 text-blue-600" />
                                            ) : (
                                                <CheckCircle className="h-5 w-5 text-purple-600" />
                                            )}
                                        </div>
                                        <div>
                                            <div className="flex items-center gap-2">
                                                <h4 className="font-medium text-gray-900">
                                                    {notif.type === 'tuition_fulfilled' ? 'Tuition Requirement Fulfilled' :
                                                        notif.type === 'job_fulfilled' ? 'Job Position Filled' : 'Application Completed'}
                                                </h4>
                                                {!notif.is_read && (
                                                    <Badge className="bg-blue-500 text-white text-xs">New</Badge>
                                                )}
                                            </div>
                                            <p className="text-sm text-gray-600 mt-1">
                                                <span className="font-medium">"{notif.content_title}"</span> was marked as fulfilled
                                            </p>
                                            {notif.remarks && (
                                                <p className="text-sm text-gray-500 mt-2 p-2 bg-white rounded border border-gray-100">
                                                    <span className="font-medium">Remarks:</span> {notif.remarks}
                                                </p>
                                            )}
                                            <div className="flex items-center gap-4 mt-2 text-xs text-gray-500">
                                                <span className="flex items-center">
                                                    <Mail className="h-3 w-3 mr-1" />
                                                    {notif.user_email || 'Unknown user'}
                                                </span>
                                                <span className="flex items-center">
                                                    <Clock className="h-3 w-3 mr-1" />
                                                    {notif.created_at ? new Date(notif.created_at).toLocaleString() : 'N/A'}
                                                </span>
                                            </div>
                                        </div>
                                    </div>
                                    <div className="flex items-center space-x-2">
                                        {!notif.is_read && (
                                            <Button
                                                size="sm"
                                                variant="outline"
                                                onClick={async () => {
                                                    try {
                                                        await apiClient.post(`/admin/notifications/${notif.id}/read`);
                                                        fetchNotifications();
                                                    } catch (error) {
                                                        toast.error('Failed to mark as read');
                                                    }
                                                }}
                                            >
                                                <CheckCircle className="h-4 w-4" />
                                            </Button>
                                        )}
                                        <Button
                                            size="sm"
                                            variant="outline"
                                            className="text-red-600 hover:bg-red-50"
                                            onClick={async () => {
                                                try {
                                                    await apiClient.delete(`/admin/notifications/${notif.id}`);
                                                    fetchNotifications();
                                                    toast.success('Notification deleted');
                                                } catch (error) {
                                                    toast.error('Failed to delete notification');
                                                }
                                            }}
                                        >
                                            <Trash2 className="h-4 w-4" />
                                        </Button>
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                )}
            </CardContent>
        </Card>
    </TabsContent>
);

export default NotificationsTab;
